import { FileRejection } from 'react-dropzone'
import { isEmpty, toLower } from 'lodash'

import { DocumentType } from '@/schema'

import { VersionStatus } from './types'

export interface FailedFileForExport {
  fileName: string
  error: string
}

const escapeCSVValue = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"` // NOSONAR - escape double quotes
  }
  return value
}

export const exportFailedFilesToCSV = (
  failedFiles: FailedFileForExport[],
  headers: string[] = ['File name', 'Error']
): string => {
  if (failedFiles.length === 0) return ''

  // Create CSV rows
  const rows = failedFiles.map((file) => {
    return [escapeCSVValue(file.fileName), escapeCSVValue(file.error)]
  })

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n')

  return csvContent
}

export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  link.remove()

  URL.revokeObjectURL(url)
}

export const generateFailedUploadsFilename = (
  prefix: string = 'Upload_Failed_List'
): string => {
  const date = new Date().toISOString().split('T')[0].replace(/-/g, '') // NOSONAR - format date as YYYYMMDD
  return `${prefix}_${date}.csv`
}

export const exportAndDownloadFailedFiles = (
  failedFiles: FailedFileForExport[],
  filename?: string,
  headers?: string[]
): void => {
  if (failedFiles.length === 0) return

  const csvContent = exportFailedFilesToCSV(failedFiles, headers)
  const downloadFilename = filename || generateFailedUploadsFilename()

  downloadCSV(csvContent, downloadFilename)
}

export interface FailedFileInfo {
  fileName: string
  error: string
}

export interface FileDropResult {
  validFiles: File[]
  failedFiles: FailedFileInfo[]
}

export const processDroppedFiles = (
  acceptedFiles: File[],
  fileRejections: FileRejection[],
  allowedExtensions: string[],
  getErrorMessage: (
    errorType: 'unsupportedExtension' | 'invalidFile' | 'default'
  ) => string
): FileDropResult => {
  const validFiles: File[] = []
  const failedFiles: FailedFileInfo[] = []

  // Process rejected files
  fileRejections.forEach((rejection) => {
    const file = rejection.file
    // Check if it's an extension issue
    if (validateFileExtension(file, allowedExtensions)) {
      const errorMessage =
        rejection.errors[0]?.message || getErrorMessage('default')
      failedFiles.push({ fileName: file.name, error: errorMessage })
    } else {
      failedFiles.push({
        fileName: file.name,
        error: getErrorMessage('unsupportedExtension'),
      })
    }
  })

  // Process accepted files - validate extension again
  acceptedFiles.forEach((file) => {
    if (validateFileExtension(file, allowedExtensions)) {
      validFiles.push(file)
    } else {
      failedFiles.push({
        fileName: file.name,
        error: getErrorMessage('unsupportedExtension'),
      })
    }
  })

  return { validFiles, failedFiles }
}

export const generateFileId = (file: File): string => {
  const randomValue = crypto.getRandomValues(new Uint32Array(1))[0].toString(36)
  return `${Date.now()}-${file.name}-${randomValue}`
}

export const validateFileExtension = (
  file: File,
  allowedExtensions: string[]
): boolean => {
  const fileName = file.name.toLowerCase()
  return allowedExtensions.some((ext) => fileName.endsWith(ext))
}

export const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.')
  if (lastDot === -1) return ''
  return filename.substring(lastDot).toLowerCase()
}

export const validateFiles = (
  files: File[],
  allowedExtensions: string[]
): {
  validFiles: File[]
  invalidFiles: Array<{ file: File; error: string }>
} => {
  const validFiles: File[] = []
  const invalidFiles: Array<{ file: File; error: string }> = []

  files.forEach((file) => {
    if (validateFileExtension(file, allowedExtensions)) {
      validFiles.push(file)
    } else {
      invalidFiles.push({
        file,
        error: `Invalid file type. Only ${allowedExtensions.join(', ')} files are allowed.`,
      })
    }
  })

  return { validFiles, invalidFiles }
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

export const getDocumentTypeFromFilename = (
  filename: string
): DocumentType | string => {
  if (isEmpty(filename)) return ''
  const firstChar = toLower(filename.charAt(0))
  switch (firstChar) {
    case 't':
      return DocumentType.TABLE
    case 'l':
      return DocumentType.LIST
    case 'f':
      return DocumentType.FIGURE
    default:
      return ''
  }
}

export const formatDocumentTypeForDisplay = (
  documentType: DocumentType | string | undefined
): string => {
  if (!documentType) return ''

  const displayMap: Record<string, string> = {
    [DocumentType.TABLE]: 'Table',
    [DocumentType.LIST]: 'Listing',
    [DocumentType.FIGURE]: 'Figure',
  }

  return displayMap[documentType] || documentType
}

export const getNewFileVersionStatus = (): VersionStatus => {
  return VersionStatus.NEW_FILE
}

export const getNewVersionStatus = (): VersionStatus => {
  return VersionStatus.NEW_VERSION
}
